import React, { useState, useMemo } from 'react';
import { FiX } from 'react-icons/fi';
import StarRating from '../Common/StarRating';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { Pie, Radar } from 'react-chartjs-2';
import './SiteRatingModal.css';

ChartJS.register(ArcElement, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler);

const DEFAULT_MOCK_RATING_CRITERIA = [
  {
    dimension: 'Site Infrastructure',
    rating: 4.5,
    subCriteria: [
      { name: 'Facilities', rating: 5.0 },
      { name: 'Equipment', rating: 4.0 },
      { name: 'Space', rating: 4.5 },
    ],
  },
  {
    dimension: 'PI Experience',
    rating: 4.0,
    subCriteria: [
      { name: 'Clinical Experience', rating: 4.5 },
      { name: 'Research Experience', rating: 3.5 },
      { name: 'Publications', rating: 4.0 },
    ],
  },
  {
    dimension: 'Patient Population',
    rating: 3.5,
    subCriteria: [
      { name: 'Patient Volume', rating: 3.0 },
      { name: 'Disease Prevalence', rating: 4.0 },
      { name: 'Recruitment Potential', rating: 3.5 },
    ],
  },
  {
    dimension: 'Regulatory Compliance',
    rating: 4.5,
    subCriteria: [
      { name: 'IRB/IEC Approval', rating: 5.0 },
      { name: 'Regulatory History', rating: 4.0 },
      { name: 'Documentation', rating: 4.5 },
    ],
  },
  {
    dimension: 'Supply Management',
    rating: 4.5,
    subCriteria: [
      { name: 'Storage', rating: 4.5 },
      { name: 'Distribution', rating: 4.5 },
    ],
  },
];

function normalizeRatingCriteriaFromSite(site) {
  const raw = site?.ratingCriteria ?? site?.RatingCriteria;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((c) => ({
    dimension: c.dimension ?? c.Dimension ?? '',
    rating: Number(c.rating ?? c.Rating ?? 0),
    subCriteria: Array.isArray(c.subCriteria ?? c.SubCriteria)
      ? (c.subCriteria ?? c.SubCriteria).map((s) => ({
          name: s.name ?? s.Name ?? '',
          rating: Number(s.rating ?? s.Rating ?? 0),
        }))
      : [],
  }));
}

const SiteRatingModal = ({ site, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const ratingCriteria = useMemo(() => {
    const fromApi = normalizeRatingCriteriaFromSite(site);
    return fromApi && fromApi.length > 0 ? fromApi : DEFAULT_MOCK_RATING_CRITERIA;
  }, [site]);

  const overallRating = ratingCriteria.reduce((sum, criteria) => sum + criteria.rating, 0) / ratingCriteria.length;
  const overallScore = Math.round(overallRating * 20); // Convert to 0-100 scale
  
  // Find top and low performing dimensions
  const sortedDimensions = [...ratingCriteria].sort((a, b) => b.rating - a.rating);
  const topDimension = sortedDimensions[0];
  const lowDimension = sortedDimensions[sortedDimensions.length - 1];

  // Pie chart data for dimension distribution
  const pieChartData = {
    labels: ratingCriteria.map(c => c.dimension),
    datasets: [{
      data: ratingCriteria.map(c => c.rating),
      backgroundColor: [
        '#2180a1',
        '#20a084',
        '#f59e0b',
        '#a84b2f',
        '#6366f1',
      ],
      borderColor: '#ffffff',
      borderWidth: 2,
    }],
  };

  // Radar chart data for performance comparison
  const radarChartData = {
    labels: ratingCriteria.map(c => c.dimension),
    datasets: [{
      label: site.siteName,
      data: ratingCriteria.map(c => c.rating),
      backgroundColor: 'rgba(33, 128, 161, 0.2)',
      borderColor: '#2180a1',
      borderWidth: 2,
      pointBackgroundColor: '#2180a1',
      pointBorderColor: '#ffffff',
      pointHoverBackgroundColor: '#1a6480',
      pointHoverBorderColor: '#ffffff',
    }],
  };

  const radarChartOptions = {
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <div className="modal-overlay rating-modal-overlay">
      <div className="site-rating-modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="site-rating-modal">
          <div className="rating-modal-header">
            <h4>Rating - {site.siteName}</h4>
            <button className="rating-modal-close" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>

          {/* Tabs */}
          <ul className="rating-nav-tabs">
            <li className="rating-nav-item">
              <button
                className={`rating-nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
            </li>
            <li className="rating-nav-item">
              <button
                className={`rating-nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Detailed Breakdown
              </button>
            </li>
            <li className="rating-nav-item">
              <button
                className={`rating-nav-link ${activeTab === 'radar' ? 'active' : ''}`}
                onClick={() => setActiveTab('radar')}
              >
                Performance Comparison Chart
              </button>
            </li>
          </ul>

          <div className="rating-modal-body">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="rating-tab-content active">
                <div className="overall-score-section">
                  <h5 className="score-title">Overall Smart Trial Score</h5>
                  <div className="overall-score-value">{overallScore} / 100</div>
                  <p className="site-rating-text">
                    Site Rating: <strong>{overallRating.toFixed(1)} / 5.0</strong>
                  </p>
                </div>

                {/* Key Highlights Cards */}
                <div className="highlights-grid">
                  <div className="highlight-card">
                    <div className="highlight-icon trophy">🏆</div>
                    <h5>Top Performing Dimension</h5>
                    <h4>{topDimension.dimension}</h4>
                    <p className="highlight-score">{topDimension.rating.toFixed(1)} / 5</p>
                  </div>
                  <div className="highlight-card">
                    <div className="highlight-icon warning">⚠️</div>
                    <h5>Needs Improvement</h5>
                    <h4>{lowDimension.dimension}</h4>
                    <p className="highlight-score">{lowDimension.rating.toFixed(1)} / 5</p>
                  </div>
                  <div className="highlight-card">
                    <div className="highlight-icon info">💡</div>
                    <h5>Best Performing Software</h5>
                    <h4>IPSM</h4>
                    <p className="highlight-score">4.5 / 5</p>
                  </div>
                </div>

                {/* Dimension Pie Chart */}
                <div className="chart-container">
                  <h5 className="chart-title">Weight Distribution</h5>
                  <div className="pie-chart-wrapper">
                    <Pie data={pieChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Breakdown Tab */}
            {activeTab === 'details' && (
              <div className="rating-tab-content active">
                <h5 className="section-title">Detailed Dimension Analysis</h5>
                {ratingCriteria.map((criteria, index) => (
                  <div key={index} className="dimension-card">
                    <div className="dimension-header">
                      <h5>{criteria.dimension}</h5>
                      <StarRating rating={criteria.rating} size={20} showValue={true} />
                    </div>
                    <div className="sub-criteria-list">
                      {criteria.subCriteria.map((sub, subIndex) => (
                        <div key={subIndex} className="sub-criteria-item">
                          <span>{sub.name}</span>
                          <StarRating rating={sub.rating} size={16} showValue={true} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Performance Comparison Chart Tab */}
            {activeTab === 'radar' && (
              <div className="rating-tab-content active">
                <h5 className="section-title">Performance Comparison Chart</h5>
                <div className="radar-chart-wrapper">
                  <Radar data={radarChartData} options={radarChartOptions} />
                </div>
              </div>
            )}
          </div>

          <div className="rating-modal-footer">
            <button className="btn btn-secondary btn-lg" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteRatingModal;
